import { AfterContentInit, AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { I18NService } from "@ebw-monorepo/i18n-translate";
import { IProjection } from "libs/ng-components/map/src/lib/interfaces/mapSettings.interface";
import { DynamicLayersFormService } from "libs/ng-components/map/src/lib/services/dynamic-layers-form.service";
import { MapService } from "libs/ng-components/map/src/lib/services/map.service";
import { Subscription } from "rxjs";

/**
 * component to display and handle single layer forms
 */
@Component({
  selector: 'ebw-monorepo-single-layer-form',
  templateUrl: './single-layer-form.component.html',
  styleUrls: ['./single-layer-form.component.scss']
})
export class SingleLayerFormComponent implements OnInit, OnDestroy {
  // input field containing the ID of the reference form
  @Input() id: string;

  // form object
  form: FormGroup;

  availableProjections: IProjection[];
  capabilityLayers: any[];
  availableFormats: string[];
  availableTileMatrixSets: string[];
  layersOptions: any[];
  layerIDs: string[];

  serviceType: string;
  
  // possible type of vector layer source
  typeVectorList: string[];

  wmtsOptionsSubscription: Subscription;

  /**
   * constructor method
   * @param _dynamicLayersFormService service to handle operations and data related to dynamic layer forms
   */
  constructor(
    private _dynamicLayersFormService: DynamicLayersFormService,
    private _i18nService: I18NService
  ) {}

  /**
   * On init lifecycle hook
   */
  ngOnInit() {
    // get the form represented by the ID of the component
    this.form = this._dynamicLayersFormService.forms.get(this.id);

    // fill the data required for the component
    this.typeVectorList = ['geojson'];
    this.serviceType = this._dynamicLayersFormService.serviceType;
    this.availableProjections = this._dynamicLayersFormService.availableProjections;
    this.availableFormats = this._dynamicLayersFormService.availableFormats;

    // setup the layer related information in case it's a WMS or WMTS layer
    if (this.serviceType === 'WMS' || this.serviceType === 'WMTS') {
      this.capabilityLayers = this._dynamicLayersFormService.capabilityLayers;
      this.layerIDs = this.capabilityLayers.map((layer) => (this.serviceType === 'WMS' ? layer.Name : layer.Identifier));
    }    

    // check that if there are WMTS options already set
    let initialWMTSOptions = this._dynamicLayersFormService.wmtsOptions.get(this.id);
    // if that's the case, extract its data and store it in the component
    if (initialWMTSOptions) {
      this.availableTileMatrixSets = initialWMTSOptions.availableTileMatrixSets;
      this.availableFormats = initialWMTSOptions.availableFormats;
    }

    // setup a subscription to WMTS options changes, to obtain the availableTileMatrixSets and availableFormats after choosing a layer
    this.wmtsOptionsSubscription = this._dynamicLayersFormService.wmtsOptionsUpdate$.subscribe(
      (options) => {
        if (options.get(this.id)) {
          this.availableTileMatrixSets = options.get(this.id).availableTileMatrixSets;
          this.availableFormats = options.get(this.id).availableFormats;
        }
      }
    );
  }

  /**
   * method to get the title of the layer based on its ID
   * @param {string} id id of the layer to extract the title of
   * @returns {string} title of the layer identified by ID
   */
  findTitle(id: string): string {
    // find the layer title in the capabilityLayers list
    let layer = this.capabilityLayers.find((el) => {
      if (this.serviceType === 'WMS') {
        return(el.Name === id);
      } else {
        return(el.Identifier === id);
      }
    });

    return(layer.Title);
  }

  public getDisplayName(projection: IProjection): string {
    let name = "";

    name += projection.code;

    if (projection.info) {
      if (projection.info.name) {
        name += ` ${projection.info.name}`;
      }
    }

    return(name);
  }

  public getTooltipInfo(projection: IProjection): string {
    let info = "";

    if (!projection) {
      return(info);
    }

    if (!projection.info) {
      return(info);
    }


    if (projection.info.datum) {
      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.datum')}: ${projection.info.datum}`;
    }
    
    if (projection.info.projection) {
      if (info.length !== 0) {
        info += "\n";
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.projection')}: ${projection.info.projection}`;
    }

    if (projection.info.zone) {
      if (info.length !== 0) {
        info += "\n";
      }

      info += `${this._i18nService.getInstantValue('map.sidebar.shape.tooltips.zone')}: ${projection.info.zone}`;
    }

    return(info);
  }

  public getSelectedTooltipInfo(code: string): string {
    let info = "";

    let foundElement = this.availableProjections.find((el) => {
      return(el.code === code);
    });

    if (!foundElement) {
      return(info);
    }

    return(this.getTooltipInfo(foundElement));
  }

  /**
   * method to remove a layer form. If there's only one layer form available, then it clears its content instead
   */
  close() {
    // check if there's only 1 form
    if (this._dynamicLayersFormService.forms.size == 1) {
      // reset the form
      this.form.reset();
      return;
    }

    // remove the form at the specified ID
    this._dynamicLayersFormService.removeForm(this.id);
  }

  /**
   * On destroy lifecycle hook
   */
  ngOnDestroy() {
    // unsubscribe from the changes of WMTS options
    this.wmtsOptionsSubscription.unsubscribe();
  }
}


